import { ComponentRef, Injectable } from '@angular/core';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { AppConfigService } from './app.config.service';

import { Store } from '@ngrx/store';
import * as appActions from "src/app/store/actions/app.actions";
import * as sessionStatusActions from "src/app/store/actions/sessionStatus.actions";
import { BrowserHelper } from '../helpers/browser.helper';
import { AppState } from '../store/states/app.state';
import { FELoggerService, LogSource } from './feLogger.service';


import { MediaStreamFactory } from 'src/libs/SipJS/platform/web';
import { ThumbVideoComponent } from '../components/video/thumb-video.component';
// import { WhiteboardSharedComponent } from '../components/video/whiteboard-shared.component';
import { AvailableDevices, MediaDevice } from '../models/device';
import { StatusChangeEvent } from '../models/statusChangeEvent';
import { OPERATOR_ROLES, PartecipantRoles } from '../models/WebServer/partecipantRoles';
import { partToStr } from '../models/WebServer/partecipantStatus';
import { I18Service } from './i18.service';
import { DeviceChangeEvent, MediaDeviceService, PreferredDevices } from './media-device.service';
import { NotificationService } from './notification.service';
import { SettingsDialogService } from './settings-dialog.service';


export type MediaEnable = { video: boolean, audio: boolean }

@Injectable({
  providedIn: 'root'
})
export class MediaStremService {

  public deviceSourceChange = new BehaviorSubject<any>(null);


  private localVideoSnapshotElement!: HTMLVideoElement;  // HTML element used to render local video preview
  
  private localAudioStream: MediaStream | undefined;
  private localVideoStream: MediaStream | undefined;

  // Promise in corso per evitare richieste concorrenti a getUserMedia
  private pendingAudioStreamPromise: Promise<MediaStream> | undefined;
  private pendingVideoStreamPromise: Promise<MediaStream> | undefined;

  public dummyVideoStream: MediaStream | undefined;
  public dummyAudioStream: MediaStream | undefined;
  private localVideoSnapshotStream: MediaStream | undefined;
  // public videoEditStream: MediaStream | undefined;
    
  private dummyImage!: HTMLImageElement;
  private dummyCanvas?: HTMLCanvasElement;

  private currentMediaEnabled?: MediaEnable;

  public remoteVideoRefs: ComponentRef<ThumbVideoComponent>[] = []

  hasMicrophonePermissionGiven?: number = undefined;
  hasCameraPermissionGiven?: number = undefined;

  private settingsDialogOpen = false;
  private destroy$ = new Subject<void>();

  constructor(
    public store: Store<AppState>,
    public config: AppConfigService,
    /*private isOnCall: IsOnCallPipe,*/
    private settingsDialogService: SettingsDialogService,
    private mediaDeviceService: MediaDeviceService
)  {
    this.initializeDeviceListener();
  }

  private initializeDeviceListener(): void {
    // Sottoscrizione ai cambiamenti di dispositivi
    this.mediaDeviceService.deviceChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: DeviceChangeEvent) => {
        this.handleDeviceChangeEvent(event);
      });
  }

  private handleDeviceChangeEvent(event: DeviceChangeEvent): void {
    const isInCall = this.config.iamOnCall;
    const isAdded = event.changes.addedDevices.length > 0;
    const isRemoved = event.changes.removedDevices.length > 0;
    const action = isAdded ? 'added' : 'removed';
    const elements = isAdded ? event.changes.addedDevices : event.changes.removedDevices;

    console.log(`mediaService - handleDeviceChangeEvent: ${event.changeType} ${action} - inCall: ${isInCall}`, elements);

    // 1. Mostra sempre notifica snackbar base
    const deviceType = I18Service.get(`SNACKBAR.DEVICE.${event.changeType}`);
    const deviceKind = event.changeType === 'headphones' ? 'audioinput' : event.changeType;
    const device = elements.find(d => d.kind === deviceKind) || elements[0];
    const msgKey = action === 'added' ? 'SNACKBAR.DEVICEADDED' : 'SNACKBAR.DEVICEREMOVED';

    NotificationService.show(I18Service.get(msgKey, { type: deviceType, devices: device.label }));

    // 2. Gestione specifica AUDIO (solo se in chiamata)
    if (event.changeType === 'audioinput' || event.changeType === 'audiooutput' || event.changeType === 'headphones') {
      if (isInCall) {
        if (isAdded) {
          // Auto-switch al nuovo dispositivo audio
          this.autoSwitchToNewAudioDevice(event.changes.addedDevices);
        } else if (isRemoved) {
          // Verifica se il dispositivo rimosso è quello in uso
          const removedDeviceInUse = this.isAudioDeviceInUse(event.changes.removedDevices);
          if (removedDeviceInUse) {
            // Switch al default (primo della lista)
            this.switchToDefaultAudioDevice();
          }
          // Se il dispositivo rimosso NON è in uso, non fare nulla
        }
      }
    }

    // 3. Gestione specifica VIDEO (solo se in chiamata)
    if (deviceKind === 'videoinput') {
      if (isInCall) {
        // Apri pannello settings per scelta esplicita (privacy)
        // sia in aggiunta che in rimozione (se quella in uso)
        if (isAdded) {
          this.openSettingsDialog();
        } else if (isRemoved) {
          // Apri solo se la webcam rimossa è quella in uso
          const removedVideoInUse = this.isVideoDeviceInUse(event.changes.removedDevices);
          if (removedVideoInUse) {
            this.openSettingsDialog();
          }
        }
      }
    }
  }

  /**
   * Verifica se un dispositivo audio rimosso è quello attualmente in uso
   */
  private isAudioDeviceInUse(removedDevices: MediaDevice[]): boolean {
    const currentAudioIn = this.mediaDeviceService.audioInputSource;
    const currentAudioOut = this.mediaDeviceService.audioOutputSource;

    return removedDevices.some(d =>
      d.deviceId === currentAudioIn ||
      d.deviceId === currentAudioOut?.deviceId
    );
  }

  /**
   * Verifica se un dispositivo video rimosso è quello attualmente in uso
   */
  private isVideoDeviceInUse(removedDevices: MediaDevice[]): boolean {
    const currentVideo = this.mediaDeviceService.videoSource;
    return removedDevices.some(d => d.deviceId === currentVideo);
  }

  /**
   * Auto-switch al nuovo dispositivo audio aggiunto (solo se in chiamata)
   */
  private autoSwitchToNewAudioDevice(addedDevices: MediaDevice[]): void {
    const newAudioInput = addedDevices.find(d => d.kind === 'audioinput');
    const newAudioOutput = addedDevices.find(d => d.kind === 'audiooutput');

    if (newAudioInput || newAudioOutput) {
      const currentPrefs = this.mediaDeviceService.getPreferredDevices();

      // Aggiorna le preferenze con il nuovo dispositivo
      this.mediaDeviceService.setPreferredDevices({
        audioInput: newAudioInput ? newAudioInput.deviceId : currentPrefs.audioInput,
        audioOutput: newAudioOutput ? newAudioOutput.deviceId : currentPrefs.audioOutput,
        camera: currentPrefs.camera
      });

      // Notifica l'utente del cambio automatico
      const deviceName = newAudioInput?.label || newAudioOutput?.label || '';
      NotificationService.show(I18Service.get('SNACKBAR.DEVICENOWUSING', { device: deviceName }));

      // Richiedi cambio stream
      this.store.dispatch(appActions.changeMediaDevice());
    }
  }

  /**
   * Switch al dispositivo audio di default (primo della lista)
   */
  private switchToDefaultAudioDevice(): void {
    const firstAudioIn = this.mediaDeviceService.getDefaultDevice('audioinput');
    const firstAudioOut = this.mediaDeviceService.getDefaultDevice('audiooutput');

    if (firstAudioIn || firstAudioOut) {
      const currentPrefs = this.mediaDeviceService.getPreferredDevices();

      this.mediaDeviceService.setPreferredDevices({
        audioInput: firstAudioIn?.deviceId || currentPrefs.audioInput,
        audioOutput: firstAudioOut?.deviceId || currentPrefs.audioOutput,
        camera: currentPrefs.camera
      });

      // Notifica l'utente del cambio automatico
      const deviceName = firstAudioIn?.label || firstAudioOut?.label || '';
      NotificationService.show(I18Service.get('SNACKBAR.DEVICENOWUSING', { device: deviceName }));

      // Richiedi cambio stream
      this.store.dispatch(appActions.changeMediaDevice());
    }
  }


  // ========================================
  // METODI WRAPPER DEPRECATI (delegano a MediaDeviceService)
  // Mantengono retrocompatibilità durante la transizione
  // ========================================

  // /** @deprecated Use mediaDeviceService.audioOutputSource instead */
  // public get audioOutputSource() { return this.mediaDeviceService.audioOutputSource; }

  // /** @deprecated Use mediaDeviceService.audioInputSource instead */
  // public get audioInputSource() { return this.mediaDeviceService.audioInputSource; }

  // /** @deprecated Use mediaDeviceService.videoSource instead */
  // public get videoSource() { return this.mediaDeviceService.videoSource; }

  // /** @deprecated Use mediaDeviceService.isMultiCamera instead */
  // public multiCamera(): boolean { return this.mediaDeviceService.isMultiCamera; }

  // /** @deprecated Use mediaDeviceService.atLeastACamera instead */
  // public atLeast1Camera(): boolean { return this.mediaDeviceService.atLeastACamera; }

  // /** @deprecated Use mediaDeviceService.getPreferredDevices() instead */
  // getPreferredDevices(): PreferredDevices {
  //   return this.mediaDeviceService.getPreferredDevices();
  // }

  savePreferredDevices(devices: PreferredDevices) {
    this.mediaDeviceService.setPreferredDevices(devices);
    this.mediaDeviceService.updateSelectedDevices(true);
    // Notifica il cambio dispositivo se siamo in chiamata
    this.store.dispatch(appActions.changeMediaDevice()); //savePreferredDevices
  }


  public async getUserMediaForEnumerateDevices():Promise<boolean>{

    return new Promise<boolean>( (res,rej)=>{
      const constraints={audio: this.hasMicrophonePermissionGiven != 1, video: this.hasCameraPermissionGiven != 1};
      if (!constraints.audio && !constraints.video) {
        res(true);
        return;
      }
      navigator.mediaDevices.getUserMedia(constraints)
      .then(stream=>{
        stream.getTracks().forEach( t => t.stop());
        this.updateDevicesListAndState('settings').then(() => { res(true); })
      })
      .catch(e => {
        rej(false);
        const causes: string[]=[];
        if(constraints.audio) causes.push('audio');
        if(constraints.video) causes.push('video');
        this.dispatchMediaPermissionError(e, {cause: causes.join('/')});
        //FELoggerService.error('mediaService - Error Enumerating devices', LogSource.MediaAudio, e)
      })
    });
  }

  private oldDeviceInfos: any;
  private oneSendAfterGetUserMedia = false;
  private dispatchAvailableDevices(): void
  {
      const devices: AvailableDevices = {
        audioInputs: this.mediaDeviceService.audioInputs,
        audioOutputs: this.mediaDeviceService.audioOutputs,
        videoInputs: this.mediaDeviceService.videoInputs
      };

      if (JSON.stringify(devices) != JSON.stringify(this.oldDeviceInfos)) {
        this.store.dispatch(appActions.retrieveDevices({availableDevices: devices}));
        FELoggerService.log(`mediaService - found devices`, LogSource.Default, devices);
        this.oldDeviceInfos = devices;
      }

      // Delega la logica di selezione dispositivi a MediaDeviceService
      // (viene già chiamato in MediaDeviceService.updateDeviceList())
      // this.mediaDeviceService.updateSelectedDevices();

      // Ottieni i dispositivi selezionati da MediaDeviceService
      const firstVideo = this.mediaDeviceService.getDefaultDevice("videoinput");
      const firstAudioOut = this.mediaDeviceService.getDefaultDevice("audiooutput");
      const firstAudioIn = this.mediaDeviceService.getDefaultDevice("audioinput");

      const afterGetUserMedia = !!firstVideo?.label && !!firstAudioIn?.label && !!firstAudioOut?.label;

      // Log informazioni dispositivi (ora usa i valori da MediaDeviceService)
      const infoDevice = `mediaService - atLeastACamera:${this.mediaDeviceService.atLeastACamera} - isMulticamera:${this.mediaDeviceService.isMultiCamera}
    Audio Output Devices - Chosen: ${this.mediaDeviceService.audioOutputSource} - ${firstAudioOut?.label}
    Audio Input Devices - Chosen: ${this.mediaDeviceService.audioInputSource} - ${firstAudioIn?.label}
    Video Devices. - Chosen: ${this.mediaDeviceService.videoSource} - ${firstVideo?.label}
    `;

      if (!this.oneSendAfterGetUserMedia) {
        FELoggerService.info(infoDevice);
      }
      this.oneSendAfterGetUserMedia = afterGetUserMedia;

      const hasMicrophonePermissionGiven = !!(this.mediaDeviceService.getDefaultDevice("audioinput")?.label) ? 1 : this.hasMicrophonePermissionGiven || 0;
      const hasCameraPermissionGiven = !!(this.mediaDeviceService.getDefaultDevice("videoinput")?.label) ? 1 : this.hasCameraPermissionGiven || 0;

      this.dispatchMediaPermission(hasMicrophonePermissionGiven, hasCameraPermissionGiven);
  }


  public init() {
    console.log(`mediaService init`);

    this.mediaDeviceService.init().then(() => {
      // Carica le preferenze salvate (ora gestite da MediaDeviceService)
      this.mediaDeviceService.loadUserPreferences();

      this.dispatchAvailableDevices();
    });
  }
  
  // Nuovo metodo per aprire il dialog delle impostazioni
  private openSettingsDialog(): void {
    if (this.settingsDialogOpen || NotificationService.s_dialogRef) {
      return; // Dialog già aperto
    }

    this.settingsDialogOpen = true;
    
    // Usa il servizio per richiedere l'apertura delle impostazioni
    this.settingsDialogService.openSettings();

    // Reset flag quando il dialog si chiude
    setTimeout(() => {
      this.settingsDialogOpen = false;
    }, 1000);
  }

  public setLocalVideoSnapshot(localVideoSnapshot: HTMLVideoElement) {
    this.localVideoSnapshotElement = localVideoSnapshot;

    this.localVideoSnapshotElement.onplaying = () => {
      // this.localVideoSnapshotPlaying = true;
      console.log(`mediaService localVideoSnapshotPlaying => true`)
    }

    this.localVideoSnapshotElement.onpause = () => {
      // this.localVideoSnapshotPlaying = false;
      console.log(`mediaService localVideoSnapshotPlaying => false`)
    }
  }
  /**
   * Start the video preview in current HTML element,
   * using direct access camera acquisition
   */
  public async startLocalVideoSnapshot() {

    console.info(`mediaService - startLocalVideoSnapshot using videoId ${this.mediaDeviceService.videoSource} with id ${this.config.AS?.switchingCameraIndex}`);
    const self = this;
    const isSafari = BrowserHelper.isSafari();
    const isAppleOS = BrowserHelper.isAppleOS();


    console.debug('mediaService - video:startLocalVideoPreview ID BEFORE: ' + this.mediaDeviceService.videoSource);
    // IMPORTANTE: Non chiedere l'audio in questo punto. Ci pensarà l'invite dopo. Serve per iphone
    let constraints;

    if (this.mediaDeviceService.canUseFacingMode()) {
      constraints = { video: { facingMode: this.mediaDeviceService.cameraFacing }, audio: false };
    } else {
      const videoSource = this.mediaDeviceService.videoSource;
      constraints = { video: videoSource ? { deviceId: { exact: videoSource } } : true, audio: false };
    }

    // self.localVideoStream = undefined;
    const videoRender = this.localVideoSnapshotElement;
    if (!videoRender) {
      FELoggerService.error(`startLocalVideoSnapshot local video render not found!!!`);
      return;
    }
    videoRender!.srcObject = null;
    console.info('mediaService - video:startLocalVideoPreview Snapshot Trying get User Media VIDEO with constarints:', constraints);//, Framework.ReinviteDirection);
    try {

      const stream: MediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      await this.updateDevicesListAndState('localVideoSnapshot');

      this.store.dispatch(sessionStatusActions.evt_changeStatus({ evt: StatusChangeEvent.MediaPermissionUpdated, val: 1 }))


      this.letStreamPlayOrLoad({videoElement:videoRender!, name: 'localVideoSnapshot'}, stream, 'snapshot');//, this.localVideoPlaying, 'Local');

      this.localVideoSnapshotStream = stream;
    } catch (reason: any) {
      FELoggerService.error('Errore start video', LogSource.MediaAudio, reason);
      FELoggerService.error(`Issue Switching Camera`, LogSource.MediaAudio, reason);
      if (reason.toString().toLowerCase().indexOf('permission denied') > -1 || reason.toString().indexOf('The request is not allowed by the user agent or the platform in the current context') > -1) {
        this.store.dispatch(sessionStatusActions.evt_changeStatus({ evt: StatusChangeEvent.MediaPermissionUpdated, val: 2 }))
      }
    }
  }

  public switchCameraForSnapshot(useSelectedCamera = false): void {
    // Delega lo switch a MediaDeviceService
    this.mediaDeviceService.switchCamera();
    this.stopLocalVideoSnapshotStream();
    this.startLocalVideoSnapshot();
  }

  public switchCameraForCall(useSelectedId = false): Promise<MediaStream> {
    // Delega lo switch a MediaDeviceService
    !useSelectedId && this.mediaDeviceService.switchCamera();
    return this.setupLocalMediaForCall(true, useSelectedId);
  }

  /**
   * Get the HTML element used to render local video (preview)
   */
  private getLocalVideoRender(): HTMLVideoElement | undefined {
    // return this.localVideoElement;
    const who = this.config.partecipantId;
    return this.getThumbVideoRender(who);
  }

  public getRemoteVideoRender(who:string | undefined): HTMLVideoElement | undefined {
    if (!who) {
      FELoggerService.error(`mediaService - remoteRenderer undefined!!`);
      return undefined;
    }
    return this.getThumbVideoRender(who);
  }

  /**
   * Get the HTML element will be used to render video coming from remote
   */
  private getThumbVideoRender(who:string): HTMLVideoElement | undefined {

    const remote = this.config.partecipantIds.find( z => z.partecipantId == who)
    const isGuest = remote?.partecipantRole == PartecipantRoles.GuestClient;
    console.log(`mediaService getThumbVideoRender ${who} guest => ${isGuest} ${remote?.partecipantRole}`)

    const t = this.remoteVideoRefs.find( z => z.instance.id == who);
    return t?.instance.video.nativeElement as HTMLVideoElement;
  }

  /**
   * Do stop Media (if any)
   */
  private stopLocalVideoRendererStream(): void {

    console.log(`mediaService - setupLocalMediaForCall - stopLocalVideoRendererStream`);
    // this.updateLocalVideoStream(undefined);

    const videoRender = this.getLocalVideoRender();
    if (!videoRender) {
      FELoggerService.error(`stopLocalVideoStream local video render not found!!!`);
      return;
    }
    
    videoRender!.pause();
    videoRender!.srcObject = null;

  }

  private stopLocalVideoStream() {
    if(this.localVideoStream) { 
      console.log(`mediaService - setupLocalMediaForCall - stopping localVideoStream: ${this.localVideoStream.id}`)
      this.localVideoStream.getTracks().forEach( t => t.stop());

      this.stopLocalVideoRendererStream();
    }
  }

  private stopLocalAudioStream() {
    if (this.localAudioStream) {
      console.log(`mediaService - stopping initial audio stream`, this.localAudioStream);
      this.localAudioStream.getTracks().forEach( t => t.stop());
      this.localAudioStream = undefined;
    }
  }

  public stopLocalVideoSnapshotStream(): void {
    FELoggerService.log(`mediaService - stopLocalVideoSnapshotStream`, LogSource.MediaAudio);
    if(this.localVideoSnapshotElement) {
      this.localVideoSnapshotElement!.pause();
      this.localVideoSnapshotElement!.srcObject = null;
    }
    if (this.localVideoSnapshotStream) {
      const tracks = this.localVideoSnapshotStream.getTracks();
      tracks.forEach(t => t.stop())
    }
  }

  public stopLocalStreams() {
    FELoggerService.log(`mediaService - stopping all streams`, LogSource.MediaAudio);
    this.stopLocalVideoStream();
    this.stopLocalAudioStream();

    this.currentMediaEnabled = undefined;

    const multistatus = [
      { evt: StatusChangeEvent.MicrophoneEnabled, val: false },
      { evt: StatusChangeEvent.VideoEnabled, val: false }
    ]
    this.store.dispatch(sessionStatusActions.evt_changeStatus({ evt: StatusChangeEvent.MultipleUpdateStatus, val: multistatus }))
  }

  /**
   * Set new Device Audio output
   */
  private changeAudioOutputDestination(htmlElement: any) {
    // Usa l'output audio selezionato da MediaDeviceService
    const audioOutDevice = this.mediaDeviceService.audioOutputSource;
    if (audioOutDevice) {
      this.attachSinkId(htmlElement, audioOutDevice);
    }
    // Altrimenti lascia il default del sistema (non chiamare setSinkId)
  }

  /**
   * Attach audio output device to video element using device/sink ID.
   */
  private attachSinkId(htmlElement: any, sink: MediaDevice) {

    const sinkId = sink?.deviceId ||'';
    if (htmlElement.setSinkId && sinkId) {
      htmlElement.setSinkId(sinkId)
        .then(() => {
          const videoname = htmlElement.getAttribute('videoname') || 'unknown';
          if (videoname == this.config.partecipantId)
            console.debug(`mediaService - attachSinkId Success, audio output device attached: ${sink.label} on element`, htmlElement.getAttribute('videoname'));
        })
        .catch((error: any) => {
          let errorMessage = error;
          if (error.name === 'SecurityError') {
            errorMessage = `You need to use HTTPS for selecting audio output device: ${error}`;
          }
          FELoggerService.error(errorMessage);
        });
    } 
  }

  private letStreamPlayOrLoad(opt:{videoElement: HTMLVideoElement, name: string}, stream: MediaStream, what: string) {//, alreadyPlay: boolean, type: string) {

    const videoElement = opt.videoElement;
    const msg = `${what} videoId [${stream.id}] of ${opt.name}`;
    if (videoElement.srcObject && (videoElement.srcObject as MediaStream).id == stream.id) {
      console.log(`mediaService - setupLocalMediaForCall- letStreamPlayOrLoad  already streaming ${msg}`);
      return
    }

    var track = stream.getVideoTracks()[0];
    console.log(`mediaService - setupLocalMediaForCall- letStreamPlayOrLoad ${msg}`, stream)

    videoElement.autoplay = true; // Safari does not allow calling .play() from a non user action
    // videoElement.srcObject = null;
    videoElement.srcObject = stream;

    // Load and start playback of media.
    // if (!stream.active || this.isFirefox) {
    const isPlaying = videoElement.currentTime > 0 && !videoElement.paused && !videoElement.ended 
        && videoElement.readyState > videoElement.HAVE_CURRENT_DATA;
    console.log(`mediaService **************** isPlaying ${isPlaying}`)

    videoElement.onload = () : void => {

    // if (!isPlaying)
        videoElement.play().catch((error: Error) => {
          FELoggerService.error(`mediaService - Failed to play media ${msg}`, LogSource.MediaAudio, error);
        });
    // }

      }

    // If a track is added, load and restart playback of media.
    stream.onaddtrack = (): void => {
      console.log(`mediaService stream addTrack`)
      videoElement.load(); // Safari does not work otheriwse
      videoElement.play().catch((error: Error) => {
        FELoggerService.error("mediaService - Failed to play remote media on add track", LogSource.MediaAudio, error);
      });
    };

    // If a track is removed, load and restart playback of media.
    stream.onremovetrack = (): void => {
      console.log(`mediaService stream removeTrack`)
      videoElement.load(); // Safari does not work otheriwse
      videoElement.play().catch((error: Error) => {
        FELoggerService.error("mediaService - Failed to play remote media on remove track", LogSource.MediaAudio, error);
      });
    };
  }

  //DummyCanvas
  private createDummyCanvas(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.dummyCanvas = document.getElementById('dummyCanvas') as HTMLCanvasElement;
      // setTimeout(() => {
      if (this.dummyCanvas) {
        const self = this;
        this.dummyImage = new Image();
        this.dummyImage.onload = () => {
          this.drawToCanvas();
          resolve()
        }
        this.dummyImage.onerror = (err: any) => {
          FELoggerService.error('mediaService - createDummyCanvas Unable to load dummy image', LogSource.MediaAudio, err);
          reject()
        }
        this.dummyImage.src = 'assets/images/no_video.png';
      } else {
        FELoggerService.error(`mediaService - createDummyCanvas dummyCanvas id not found!`)
        reject();
      }
      // }, 500);
    });
  }

  private drawToCanvas() {
    if (!this.dummyCanvas) return;
    console.log(`mediaService - drawToCanvas`)
    const dummyCanvas = this.dummyCanvas;
    const dummyImage = this.dummyImage

    var canvasContext = this.dummyCanvas.getContext("2d");
    if (!canvasContext) return;

    (function loop() {
      canvasContext.drawImage(dummyImage, 0, 0, 640, 480, 0, 0, dummyCanvas.width, dummyCanvas.height);
      window.setTimeout(loop, 1000); // drawing at 1fps
    })();
  }

  private async createDummyVideoStream(initial = false): Promise<void> {
    return new Promise((resolve, reject) => {
      if(this.dummyVideoStream){
        resolve();
        return;
      }
      this.createDummyCanvas()
      .then(() => {
        if (!this.dummyCanvas) {
          FELoggerService.error(`why dummyCanvas is null???`)
          reject();
          return;
        }
        this.dummyVideoStream = this.dummyCanvas.captureStream(1);
        this.dummyCanvas.width = 640;
        this.dummyCanvas.height = 480;
  
        const tracks = this.dummyVideoStream.getVideoTracks();
        console.log(`mediaService - create dummy video tracks ${tracks.length}`);
        resolve();
      })
      .catch(()=>{reject();});


      // const videoStream 
    });
  }

  private async createDummyAudioStream(initial = false): Promise<void> {
    return new Promise(async (resolve, reject) => {
      if(this.dummyAudioStream){
        resolve();
        return;
      }

      this.dummyAudioStream = await this.createSilentAudioStream();
      console.log(`mediaService - create dummy audio stream`, this.dummyAudioStream);
      resolve()

    })
  }


  public localMediaStreamFactory(): MediaStreamFactory { // in base allo stato "restituisce" dummy o webcam (richiamata da sip-service!)
    console.log(`mediaService - localMediaStreamFactory request from sipService`)
    const self = this;

    return (constraints: MediaStreamConstraints): Promise<MediaStream> => {
      const x: MediaEnable = {
        audio: !!constraints.audio,
        video: !!constraints.video
      }
      return self.setupLocalMediaForCall()//x);
    };
  }

  public isCurrentMediaChanged() {
    const audioEnabled = !!this.config.AS?.microphoneEnabled; //muto
    const videoEnabled = !!this.config.AS?.videoEnabled; // se disabilitato si va di dummy!!

    return this.currentMediaEnabled == undefined || this.currentMediaEnabled.audio != audioEnabled || this.currentMediaEnabled.video != videoEnabled;

  }

  public setupLocalMediaForCall(switchCamera = false, useSelectedId = false): Promise<MediaStream> {

    const selfThis = this;

    const audioEnabled = !!selfThis.config.AS?.microphoneEnabled; //muto
    const videoEnabled = !!selfThis.config.AS?.videoEnabled; // se disabilitato si va di dummy!!


///sinkid (output audio)
    //this.changeAudioOutputDestination();
    // if(this.remoteVideoRefs && this.remoteVideoRefs.length) {
    //   const el = this.remoteVideoRefs[0].instance.video.nativeElement;
    //   if (!el.setSinkId) {
    //     FELoggerService.warn(`${BrowserHelper.getBrowserInfo()} does not support output device selection.`);
    //   }
    // }
    this.remoteVideoRefs.forEach( ref => {
      this.changeAudioOutputDestination(ref.instance.video.nativeElement);
    })


    // const currentMediaEnabled = {...this.currentMediaEnabled};
    const useInitialAudio = (this.currentMediaEnabled?.audio === audioEnabled) && !useSelectedId;
    const useInitialVideo = (this.currentMediaEnabled?.video === videoEnabled) && !switchCamera;

    console.log(`mediaService - setupLocalMediaForCall audio: ${audioEnabled} (initial: ${useInitialAudio}) - video: ${videoEnabled} (initial: ${useInitialVideo}) - prev: ${JSON.stringify(this.currentMediaEnabled)}`);

    this.currentMediaEnabled = {audio: audioEnabled, video: videoEnabled};

    const remoteStream = new MediaStream();
    return new Promise<MediaStream>( (resolve, reject) => {
      this.getLocalAudioStream(useInitialAudio).then( (audiostream: MediaStream) => {
          audiostream.getAudioTracks().forEach( t => {console.log(`DEBUG- mediaService audio track ${t.id}`); remoteStream!.addTrack(t)});//.clone())});
          
          selfThis.getLocalVideoStream(useInitialVideo).then( (videoStream: MediaStream) => {
              videoStream.getVideoTracks().forEach( t => remoteStream!.addTrack(t));//.clone()));
              resolve(remoteStream!);

              //fix #58172
              const audio = (this.currentMediaEnabled?.audio) ? 1 : this.hasMicrophonePermissionGiven || 0;
              const video = (this.currentMediaEnabled?.video) ? 1 : this.hasCameraPermissionGiven || 0;
              selfThis.dispatchMediaPermission(audio, video);
            })
            .catch((reason) => {            
              
              selfThis.dispatchMediaPermissionError(reason, {cause: 'video'});
              if (switchCamera && !useSelectedId){ // reimposta la precedente???
                  selfThis.mediaDeviceService.switchCameraRollback();
              }
              reject(reason)})
        })
        .catch((reason) => {
          selfThis.dispatchMediaPermissionError(reason, {cause: 'audio'});
          reject(reason)
        })
    });

  }

  private dispatchMediaPermissionError(reason: any, what: {cause: string}){//audioEnabled: boolean, videoEnabled: boolean) {
    FELoggerService.error(`mediaService - setupLocalMedia ERROR for ${what.cause}`, LogSource.MediaAudio, reason);
    
    const problems = ['permission denied', 'the request is not allowed by the user agent or the platform in the current context'];

    if (problems.some(p => reason.toString().toLowerCase().indexOf(p) > -1)) {
      
      NotificationService.popupWarn({msg:I18Service.get('SNACKBAR.MEDIAPERMISSIONREQUIRED')});

      //fix #58172
      const audio = (what.cause === 'audio') ? 2 : this.hasMicrophonePermissionGiven || 0;
      const video = (what.cause === 'video') ? 2 : this.hasCameraPermissionGiven || 0;

      this.dispatchMediaPermission(audio, video);

    } else {
      NotificationService.popupWarn({msg:I18Service.get('SNACKBAR.MEDIADENIED', {device: what.cause})});
    }
  }
  private dispatchMediaPermission(audio: number, video: number){//permissions: { audio?: number, video?: number }) {

    const toDispatch = this.hasCameraPermissionGiven != video || this.hasMicrophonePermissionGiven != audio;
    if (!toDispatch) { return; }

    const multiStatus: any = [];

    this.hasCameraPermissionGiven = video;
    this.hasMicrophonePermissionGiven = audio;

    multiStatus.push({ evt: StatusChangeEvent.MicrophonePermissionUpdated, val: audio })
    multiStatus.push({ evt: StatusChangeEvent.MediaPermissionUpdated, val: video })

    console.log(`mediaService - dispatching permission mic: ${audio} - camera ${video}`);
    this.store.dispatch(sessionStatusActions.evt_changeStatus({ evt: StatusChangeEvent.MultipleUpdateStatus, val: multiStatus }));

  }

  private getLocalAudioStream(useLocal: boolean): Promise<MediaStream> {
    const selfThis = this;
    const audioEnabled = !!this.config.AS?.microphoneEnabled; //muto
    // const useLocal = (this.currentMediaEnabled?.audio === audioEnabled);
    return new Promise( async (resolve, reject) => {
      if (audioEnabled && this.localAudioStream && useLocal) {
        console.log(`DEBUG- mediaService - reuse initialAudioStream`)
        resolve(this.localAudioStream);
        return;
      }
      if(this.currentMediaEnabled?.audio) { // prende mic

        // Se c'è già una richiesta getUserMedia in corso, riutilizza quella promise
        if (this.pendingAudioStreamPromise) {
          console.log(`mediaService - reusing pending audio stream promise`);
          this.pendingAudioStreamPromise.then(resolve).catch(reject);
          return;
        }

        // Usa il dispositivo selezionato da MediaDeviceService
        const audioInputSource = this.mediaDeviceService.audioInputSource;
        const constraints = audioInputSource
          ? { audio: { deviceId: { exact: audioInputSource } } }
          : { audio: true }; // true = usa il default del sistema

        this.stopLocalAudioStream();
        console.log(`DEBUG- mediaService - audio mic access setting initialAudioStream with constraints:`, constraints);

        // Salva la promise in corso
        this.pendingAudioStreamPromise = navigator.mediaDevices.getUserMedia(constraints)
          .then((stream) => {
            this.localAudioStream = stream;
            return this.updateDevicesListAndState('localAudioStream').then(() => stream);
          })
          .catch((e) => {
            console.error('mediaService - mic problem', e);
            this.currentMediaEnabled!.audio = false;
            this.store.dispatch(appActions.toggleMicrophone({enabled: false}) );
            throw e;
          })
          .finally(() => {
            // Pulisci la promise quando completa (successo o errore)
            this.pendingAudioStreamPromise = undefined;
          });

        // Restituisci la promise salvata
        this.pendingAudioStreamPromise.then(resolve).catch(reject);
        return;
      } 

      //use dummy audio
      console.log(`mediaService - audio dummy`)
      if (!this.dummyAudioStream) {
        console.log('mediaService - trying dummy audio creation')
        console.time('mediaService - dummyStream - dummyaudio');
        await this.createDummyAudioStream();
        console.timeEnd('mediaService - dummyStream - dummyaudio')
      }
      this.stopLocalAudioStream();
      console.log('mediaService - cloning dummy audio')

      const stream = new MediaStream();
      this.dummyAudioStream?.getAudioTracks().forEach(t => {
        const c = t.clone(); // per evitare che lo stopStream fermi anche il dummy
        stream.addTrack(c);
      });
      
      //this.initialAudioStream = stream;
      resolve(stream);
    })
  }

  private getLocalVideoStream(useLocal: boolean): Promise<MediaStream>{
    const selfThis = this;
    return new Promise( async (resolve, reject) => {
      if (useLocal) {
        // if (this.videoEditStream) {  // nel caso sia in editing video
        //   console.log(`mediaService - reuse videoEditStream`)
        //   resolve(this.videoEditStream);
        //   return;
        // }
        if(this.localVideoStream) {
          console.log(`mediaService - setupLocalMediaForCall - reuse remoteStream`)
          resolve(this.localVideoStream);
          return;
        }
      }

      if(this.currentMediaEnabled?.video) { // prende webcam

        // if (this.videoEditStream) {  // nel caso sia in editing video
        //   console.log(`mediaService - use videoEditStream`)
        //   resolve(this.videoEditStream);
        //   return;
        // }

        // Se c'è già una richiesta getUserMedia in corso, riutilizza quella promise
        if (this.pendingVideoStreamPromise) {
          console.log(`mediaService - reusing pending video stream promise`);
          this.pendingVideoStreamPromise.then(resolve).catch(reject);
          return;
        }

        let constraints: MediaStreamConstraints;

        if (this.mediaDeviceService.canUseFacingMode()) {
          // Mobile: usa il valore corrente di cameraFacing da MediaDeviceService
          constraints = { video: { facingMode: this.mediaDeviceService.cameraFacing } };
        } else {
          // Desktop: usa il valore corrente di videoSource da MediaDeviceService
          const videoSource = this.mediaDeviceService.videoSource;
          constraints = videoSource
            ? { video: { deviceId: { exact: videoSource } } }
            : { video: true }; // default del sistema
        }

        this.stopLocalVideoStream();
        // this.stopLocalVideoRendererStream();
        //console.log(`mediaService - setupLocalMediaForCall - video webcam access ${JSON.stringify(constraints.video)}`)

        // Salva la promise in corso
        this.pendingVideoStreamPromise = navigator.mediaDevices.getUserMedia(constraints)
          .then((stream) => {
            console.log(`mediaService - setupLocalMediaForCall - video webcam access ${JSON.stringify(constraints.video)} => got stream ${stream.id}`)
            this.updateLocalVideoStream(stream);
            return this.updateDevicesListAndState('localVideoStream').then(() => stream);
          })
          .catch((e) => {
            FELoggerService.error('mediaService - video problem', LogSource.MediaAudio, e);
            this.currentMediaEnabled!.video = false;
            this.store.dispatch(appActions.toggleVideo({}));
            throw e;
          })
          .finally(() => {
            // Pulisci la promise quando completa (successo o errore)
            this.pendingVideoStreamPromise = undefined;
          });

        // Restituisci la promise salvata
        this.pendingVideoStreamPromise.then(resolve).catch(reject);
        return;
      } 

      //use dummy audio
      console.log(`mediaService - video dummy`)
      if (!this.dummyVideoStream) {
        console.log('mediaService - trying dummy video creation')
        
        console.time('mediaService - dummyStream - dummyvideo')
        await this.createDummyVideoStream();
        console.timeEnd('mediaService - dummyStream - dummyvideo')
      }
      this.stopLocalVideoStream();
      // this.stopLocalVideoRendererStream();
      const stream = this.cloneStream(this.dummyVideoStream, 'dummyVideo');
      this.updateLocalVideoStream(stream);

      resolve(stream);
    })
  }

  private cloneStream(s: MediaStream | undefined, what: string): MediaStream  {
    console.log(`MediaService - cloning stream ${what} - with ${s?.getTracks().length} tracks`);
    const stream = new MediaStream();
    s?.getTracks().forEach( t => {
      const c = t.clone(); // per evitare che lo stopStream fermi anche il dummy
      console.log(`MediaService cloned track ${c.kind} [${c.id}]`)
      stream.addTrack(c);
    })
    return stream
  }

  private updateLocalVideoStream(stream: MediaStream | undefined) {
    // this.stopCurrentVideoStream();
    console.log(`mediaService - setupLocalMediaForCall - update localVideoStream: ${stream?.id}`)
    this.localVideoStream = stream; 

    const myLocalVideo = this.getLocalVideoRender();
    stream && this.letStreamPlayOrLoad({videoElement:myLocalVideo!, name:`update my localvideo`}, stream, 'video');//????
    
    // this.remoteStream = new MediaStream();
    // stream && stream.getTracks().forEach( t => this.remoteStream?.addTrack(t.clone()));

  }

  private async createSilentAudioStream(): Promise<MediaStream> {
    try {
      const audioContext = new AudioContext();
      
      // Se è sospeso, tenta resume e controlla con timeout
      if (audioContext.state === 'suspended') {
        await this.tryResumeAudioContext(audioContext);
      }
      
      // Crea l'oscillatore silenzioso
      return this.createSilentStream(audioContext);
      
    } catch (e: any) {
      console.warn('AudioContext creation failed:', e);
      
      // Gestisce il caso specifico di user gesture richiesta
    if (e.message === 'NotAllowedError') {
      // NON fare fallback - attendi la user gesture
      await this.waitForUserGesture();
      // Riprova dopo la gesture
      return this.createSilentAudioStream();
    }
      
      return new MediaStream(); // Fallback
    }
  }
  private async tryResumeAudioContext(audioContext: AudioContext): Promise<void> {
    return new Promise((resolve, reject) => {
      // Tentativo di resume
      audioContext.resume();
      
      // Controllo con timeout
      setTimeout(() => {
        if (audioContext.state === 'suspended') {
          reject(new Error('NotAllowedError'));
        } else {
          resolve();
        }
      }, 200);
    });
  }

  private createSilentStream(audioContext: AudioContext): MediaStream {
    const oscillator = audioContext.createOscillator();
    oscillator.frequency.value = 0;
    const dst = audioContext.createMediaStreamDestination();
    oscillator.connect(dst);
    oscillator.start();

    const track = dst.stream.getAudioTracks()[0];
    track.enabled = false;

    return new MediaStream([track]);
  }
  // Metodo separato per gestire l'errore di user gesture
  onePendingUserGesture: Promise<void> | null = null;
  private async waitForUserGesture(): Promise<void> {
    if (this.onePendingUserGesture) {
      return this.onePendingUserGesture;
    }
    this.onePendingUserGesture = new Promise((resolve) => {
      console.log('Mostra popup per user gesture');
      
      NotificationService.popupWarn({
        msg: I18Service.get('POPUP.CLICKFORPLAY'),
        //blocking: true

      }).then((result) => {
        console.log(`User gesture ${result} ricevuta, riprovo...`);
        this.onePendingUserGesture = null;
        resolve();
      });
    });
    return this.onePendingUserGesture;
  }


  private prevZoomId: ComponentRef<ThumbVideoComponent>| undefined;
  public async setVideoZoom(id: string){
    
    if(id) {
      const prevZoomRef = this.remoteVideoRefs.find( z => z.instance.zoom);
      if (prevZoomRef) this.prevZoomId = prevZoomRef;
    }

    this.remoteVideoRefs.forEach( z => z.instance.zoom = false)
    // this.setWhiteboardZoom(false)

    // if (id == Constants.WHITEBOARD_SHARED_ID) {
    //   this.setWhiteboardZoom(true);
    //   return; // non zoom su whiteboard
    // }


    // setTimeout( () => {
    var ref = this.remoteVideoRefs.find( z => z.instance.id == id);
    if (!ref && this.prevZoomId) {
      ref = this.prevZoomId;
    }
    if (ref ) {
      // this.actualThumbId = id;
      ref.instance.zoom = true;
      try {
        await ref.instance.video.nativeElement.play();
      } catch(e: any){
        console.warn(`Play video failed for ${ref.instance.name}:`, e);
        if (e.name === 'NotAllowedError') {
          NotificationService.popupWarn({msg:I18Service.get('POPUP.CLICKFORPLAY')}).then(()=> {
            ref!.instance.video.nativeElement.play();
          });
        }
        
      }
      
      if(!this.prevZoomId)this.prevZoomId=ref; //primo set del prev

      console.log(`mediaService - setFullVideo zoomId ${id} ${ref.instance.name}`, this.remoteVideoRefs);

      if (this.config.isReceiver) {
        this.store.dispatch(appActions.operatorZoomId({id}));
        //this.store.dispatch(sessionStatusActions.cmd_dispatch({cmd: CommandEvent.OperatorZoomId,val:id, toIds: appActions.SEND_TO_ALL}))
      }

    }
    return ref;
  }

  /** @deprecated Use mediaDeviceService.canUseFacingMode() instead */
  private canUseFacingMode(): boolean {
    return this.mediaDeviceService.canUseFacingMode();
  }

  public fitVideoTo(id: string, value: boolean){
    
    var ref = this.remoteVideoRefs.find( z => z.instance.id == id);
    if (ref) {
      ref.instance.toolbar.videoCover = value;
    }
  }

  setZoomOnTalking() {

    // mantiene lo zoom precedente anche se video disattivato (ma deve essere in chiamata)
    const zooming = this.remoteVideoRefs.find( z => z.instance.zoom && z.instance.onCall);
    if (zooming) { 
       const videoEnabled = this.config.partecipantsStatus$.find( z => z.partecipantId == zooming.instance.id && z.videoEnabled);
       if(videoEnabled) { // se ha il video attivo mantieni lo zoom
         console.log(`mediaService - setupLocalMediaForCall - setZoomOnTalking: already zooming ${partToStr(videoEnabled)}`)
         return;
       } 
    }

    // prende lo zoomoId dell'operatore (ma deve essere in chiamata con video attivo)
    console.log(`mediaService - this.config.AS?.operatorZoomId => ${this.config.AS?.operatorZoomId}`)
    const operatorZoom = this.config.partecipantsStatus$.find( z => z.partecipantId == this.config.AS?.operatorZoomId && this.config.onCallPipe.transform(z.phoneStatus, true))
    if(operatorZoom) {
      console.log(`mediaService - setupLocalMediaForCall - setZoomOnTalking: setting zoom on same of OPERATOR ${partToStr(operatorZoom)}`)
      this.setVideoZoom(operatorZoom.partecipantId);
      return
    }

    // verifica se utente è in call.. 
    const caller = this.config.partecipantsStatus$.find( z => z.partecipantRole == PartecipantRoles.SecondaryClient && this.config.onCallPipe.transform(z.phoneStatus, true))
    if (caller) {
      console.log(`mediaService - setupLocalMediaForCall - setZoomOnTalking: setting zoom on USER ${partToStr(caller)}`)
      this.setVideoZoom(caller.partecipantId);
      return
    }

    // verifica se un operatore è in call.. con video attivo!!
    const operator = this.config.partecipantsStatus$.find( z => OPERATOR_ROLES.includes( z.partecipantRole ) && z.videoEnabled)
    if (operator) {
      console.log(`mediaService - setupLocalMediaForCall - setZoomOnTalking: setting zoom on OPERATOR ${partToStr(operator)}`)
      this.setVideoZoom(operator.partecipantId);
      return
    }

    // alltrimenti metti il primo che ha il video abilitato
    const firstVideoEnabled = this.config.partecipantsStatus$.find( z => z.videoEnabled);
    if (firstVideoEnabled) {
      console.log(`mediaService - setupLocalMediaForCall - setZoomOnTalking: setting zoom on first video enabled ${partToStr(firstVideoEnabled)}`)
      this.setVideoZoom(firstVideoEnabled.partecipantId);
      return
    }

    // verifica se sono io in chiamata e nel caso metti me
    // const me =  this.config.partecipantsStatus$.find( z => z.partecipantId == this.config.partecipantId && z.videoEnabled)
    if (this.config.iamOnCall) {
      console.log(`mediaService - setupLocalMediaForCall - setZoomOnTalking: setting zoom on me: ${partToStr(this.config.myPartecipantStatus)}`)
      this.setVideoZoom(this.config.partecipantId);
      return
    }

    console.log(`mediaService - setupLocalMediaForCall - setZoomOnTalking - no one with video enabled`)
  }


  private async updateDevicesListAndState(who: string): Promise<void> {
    await this.mediaDeviceService.updateDeviceList();
    this.dispatchAvailableDevices();
  }
}
